/**
 * Detects sustained API unavailability (M1-29) and drives the maintenance
 * page — a module-level store, not a hook, mirroring the customer app's
 * `lib/maintenance.ts` exactly (see its own header for the full reasoning on
 * every transition). `apiClient`'s middleware is plain code outside React
 * and needs one place to REPORT a failure that a component can subscribe to;
 * `MaintenanceGate` (`routes/Maintenance.tsx`) is the one subscriber.
 *
 * State machine summary:
 * - `ok` → `checking`: `apiClient`'s `onError` (a network-level failure —
 *   connection refused, DNS failure, exactly what a torn-down API container
 *   gives) or a gateway status, 502/503/504 (see `api/client.ts`'s own note —
 *   a torn-down API surfaces to the browser as a 502 from the PROXY in front
 *   of it, not a network-level failure). A business 500 is never a trigger.
 * - `checking` → `ok`: the confirming `/health` call succeeded — isolated,
 *   not the platform. Renders identically to `ok`; nothing shown.
 * - `checking` → `down`: `/health` failed too — sustained, not a blip.
 * - `down` → `ok`: a later `/health` poll (exponential backoff) succeeds, or
 *   the maintenance flag clears.
 *
 * Re-entrancy: `reportApiUnavailable` is a no-op unless the state is
 * currently `ok` — five failing audited reads in the same render must not
 * launch five confirming `/health` calls.
 */
import { checkHealth } from './health';
import { fetchMaintenanceFlag } from './maintenanceFlag';

export type MaintenanceStatus = 'ok' | 'checking' | 'down';

export interface MaintenanceState {
  status: MaintenanceStatus;
  /** Set only when `status === 'down'`: which signal put it there. */
  reason?: 'api' | 'flag';
  /** The flag file's own message, when `reason === 'flag'`. */
  message?: string;
}

const INITIAL_BACKOFF_MS = 3_000;
const MAX_BACKOFF_MS = 30_000;
const FLAG_POLL_INTERVAL_MS = 30_000;

const OK: MaintenanceState = { status: 'ok' };

let state: MaintenanceState = OK;
let backoffMs = INITIAL_BACKOFF_MS;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let flagPollTimer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();
const authRequiredListeners = new Set<() => void>();

function setState(next: MaintenanceState) {
  state = next;
  for (const listener of listeners) {
    listener();
  }
}

export function subscribeMaintenance(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getMaintenanceSnapshot(): MaintenanceState {
  return state;
}

/** Called by `apiClient`'s middleware on a network-level failure or a 503. */
export function reportApiUnavailable(): void {
  if (state.status !== 'ok') {
    return;
  }
  setState({ status: 'checking' });
  void confirmOutage();
}

async function confirmOutage(): Promise<void> {
  const healthy = await checkHealth();
  // Still `checking` unless the flag poll already moved it on — a flag flip
  // mid-confirmation must win either way, not be clobbered back to `ok` OR
  // overwritten with `reason: 'api'`.
  if (state.status !== 'checking') {
    return;
  }
  if (healthy) {
    setState(OK);
    return;
  }
  backoffMs = INITIAL_BACKOFF_MS;
  setState({ status: 'down', reason: 'api' });
  scheduleHealthPoll();
}

function scheduleHealthPoll(): void {
  if (pollTimer) {
    return;
  }
  pollTimer = setTimeout(() => {
    pollTimer = null;
    void (async () => {
      const healthy = await checkHealth();
      // The flag might be what's actually holding this down now; a healthy
      // API does not override a deliberate maintenance window.
      if (healthy && state.reason !== 'flag') {
        backoffMs = INITIAL_BACKOFF_MS;
        setState(OK);
        return;
      }
      if (state.status === 'down' && state.reason === 'api') {
        backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
        scheduleHealthPoll();
      }
    })();
  }, backoffMs);
}

/**
 * Starts polling the maintenance flag (the "deliberate switch", the AC's own
 * words) on a standing interval — safe to run continuously, unlike the
 * health check, since it is one small static file rather than a database
 * round trip. Call once, near the app's root; returns a cleanup function.
 */
export function startMaintenanceFlagPolling(intervalMs = FLAG_POLL_INTERVAL_MS): () => void {
  let cancelled = false;

  async function tick() {
    if (cancelled) {
      return;
    }
    const flag = await fetchMaintenanceFlag();
    if (cancelled) {
      return;
    }
    if (flag.maintenance) {
      setState({ status: 'down', reason: 'flag', message: flag.message });
    } else if (state.status === 'down' && state.reason === 'flag') {
      setState(OK);
    }
  }

  void tick();
  flagPollTimer = setInterval(() => void tick(), intervalMs);
  return () => {
    cancelled = true;
    if (flagPollTimer) {
      clearInterval(flagPollTimer);
      flagPollTimer = null;
    }
  };
}

/** Called by `apiClient`'s middleware on a 401 — never treated as maintenance. */
export function reportAuthRequired(): void {
  for (const listener of authRequiredListeners) {
    listener();
  }
}

export function subscribeAuthRequired(listener: () => void): () => void {
  authRequiredListeners.add(listener);
  return () => authRequiredListeners.delete(listener);
}

/** Test-only: clears module-level state between tests. Not for app code. */
export function __resetMaintenanceForTests(): void {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  if (flagPollTimer) {
    clearInterval(flagPollTimer);
    flagPollTimer = null;
  }
  backoffMs = INITIAL_BACKOFF_MS;
  state = OK;
  listeners.clear();
  authRequiredListeners.clear();
}
